/**
 * Shared TanStack Query defaults.
 *
 * Strategy:
 * - Network errors / 5xx: retry up to 3 times with exponential backoff.
 * - 4xx (auth/validation): do NOT retry.
 * - Mutations: do not retry by default (most are non-idempotent).
 *
 * Apply via: `new QueryClient({ defaultOptions: queryClientDefaults })`.
 */
import type { DefaultOptions } from "@tanstack/react-query";

const MAX_RETRIES = 3;

function getStatus(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const e = error as { status?: number; statusCode?: number; response?: { status?: number } };
  return e.status ?? e.statusCode ?? e.response?.status ?? null;
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  const status = getStatus(error);
  // 4xx: don't retry (auth/validation/not-found)
  if (status !== null && status >= 400 && status < 500) return false;
  // network error or 5xx: retry
  return true;
}

export const queryClientDefaults: DefaultOptions = {
  queries: {
    retry: shouldRetry,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  },
  mutations: {
    // Non-idempotent by default → no retry.
    retry: false,
  },
};
